// The business KPI report (public.dashboard_business_kpis, rnrinfra migrations 20260930194734_business_kpis and
// 20261001144854_hub_today, which added the client pick and each client's account manager) and the
// client commercial record (dashboard_client_terms / dashboard_save_client_terms). Admins and super admins only.

export type Stage = 'unknown' | 'trial' | 'paying' | 'paused' | 'cancelled' | 'trial_ended';
export type BillingModel = 'monthly' | 'per_lead' | 'hybrid';
export type Pace = 'met' | 'ahead' | 'on_track' | 'behind' | 'no_goal';

export type KpiClient = {
  business_id: string; client_name: string; agency_name: string; manager: string | null; stage: Stage; relationship_status: string;
  billing_model: BillingModel | null; monthly_fee: number | null; price_per_lead: number | null;
  leads: number; paid_leads: number; organic_leads: number;
  goal: number | null; expected: number | null; projected_leads: number | null; pace: Pace;
  revenue: number; projected_revenue: number; terms_missing: boolean;
  costs: { google: number; meta: number; organic: number; other: number; total: number; google_synced: boolean; meta_synced: boolean };
  gross_margin: number; margin_pct: number | null;
};
export type KpiTrial = {
  business_id: string; client_name: string; agency_name: string; manager: string | null; started_on: string; days: number;
  lead_goal: number | null; leads: number; paid_leads: number; organic_leads: number;
  closer: string | null; talked_on: string | null; client_monthly_revenue: number | null;
  monthly_fee: number | null; price_per_lead: number | null;
};
export type BusinessKpis = {
  month: string; timezone: string; generated_at: string; days_in_month: number; share_of_month: number;
  /** True when the report covers only the clients picked; the billing goal is still the agency's whole goal. */
  picked: boolean;
  agencies: { agency_id: string; agency_name: string; billing_goal: number | null; goal_revision: number }[];
  billing: { current: number; projected: number; goal: number | null; paying_clients: number; costs: number; gross_margin: number; terms_missing: string[] };
  leads: { total: number; paid: number; organic: number };
  clients: KpiClient[];
  trials: KpiTrial[];
  trial_results: {
    month: { converted: number; unsuccessful: number; abandoned: number };
    all_time: { converted: number; unsuccessful: number; abandoned: number; active: number };
    list: { business_id: string; client_name: string; outcome: 'converted' | 'unsuccessful' | 'abandoned'; started_on: string | null; ended_on: string; leads: number; closer: string | null }[];
  };
  closers: { closer_id: string; name: string; active_trials: number; talked_month: number; closed_month: number; talked_all: number; closed_all: number }[];
  cancellations: {
    month: { business_id: string; client_name: string; manager: string | null; cancelled_on: string; reason: string; monthly_value: number; still_receiving_leads: boolean }[];
    last_90_days: number;
  };
  spend: { google: number; meta: number; unmatched_synced: number | null; last_synced_on: string | null };
  gaps: { stage_unknown: number; no_goal: number; cancelled_still_live: number };
};

export type ClientTerms = {
  business_id: string; stage: Stage; billing_model: BillingModel | null; monthly_fee: number | null; price_per_lead: number | null;
  monthly_lead_goal: number | null; client_monthly_revenue: number | null; trial_started_on: string | null; trial_lead_goal: number | null;
  trial_ended_on: string | null; trial_outcome: 'converted' | 'unsuccessful' | 'abandoned' | null; closer_id: string | null;
  talked_on: string | null; closed_on: string | null; paying_since: string | null; cancelled_on: string | null; cancel_reason: string | null;
  notes: string | null; revision: number; updated_at?: string;
};
export type ClientTermsData = { business_id: string; client_name: string; relationship_status: string; terms: ClientTerms; closers: { id: string; name: string }[] };

export const stageLabels: Record<Stage, string> = {
  unknown: 'Not set', trial: 'On trial', paying: 'Paying', paused: 'Paused', cancelled: 'Cancelled', trial_ended: "Trial didn't convert",
};
export const billingLabels: Record<BillingModel, string> = { monthly: 'Monthly fee', per_lead: 'Per lead', hybrid: 'Monthly fee + per lead' };
export const paceLabels: Record<Pace, string> = { met: 'Target met', ahead: 'Ahead', on_track: 'On track', behind: 'Behind', no_goal: 'No target' };
export const paceTone: Record<Pace, 'green' | 'blue' | 'neutral' | 'red' | 'muted'> = { met: 'green', ahead: 'blue', on_track: 'neutral', behind: 'red', no_goal: 'muted' };
export const outcomeLabels = { converted: 'Converted', unsuccessful: 'Unsuccessful', abandoned: 'Abandoned' } as const;

export const money = (value: number | null | undefined, cents = false) =>
  value === null || value === undefined ? '—' : value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: cents ? 2 : 0, minimumFractionDigits: cents ? 2 : 0 });
export const count = (value: number | null | undefined) => (value === null || value === undefined ? '—' : Math.round(value).toLocaleString('en-US'));
export const monthLabel = (month: string) => new Date(`${month.slice(0, 7)}-15T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
export const dateLabel = (day: string | null) => (day ? new Date(`${day}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) : '—');

/** Share of the goal reached, capped for bars; null without a goal. */
export function goalShare(value: number, goal: number | null): number | null {
  if (!goal || goal <= 0) return null;
  return Math.max(0, Math.min(1, value / goal));
}

/** Behind first, then no goal, on track, ahead and met; within a pace, the furthest behind the expected count first. */
export function sortByPace(clients: KpiClient[]): KpiClient[] {
  const rank: Record<Pace, number> = { behind: 0, no_goal: 1, on_track: 2, ahead: 3, met: 4 };
  return [...clients].sort((a, b) => rank[a.pace] - rank[b.pace]
    || ((a.leads - (a.expected ?? a.leads)) - (b.leads - (b.expected ?? b.leads)))
    || a.client_name.localeCompare(b.client_name));
}

/** Lowest margin first; clients billed nothing and costing nothing last. */
export function sortByMargin(clients: KpiClient[]): KpiClient[] {
  const idle = (c: KpiClient) => c.revenue === 0 && c.costs.total === 0;
  return [...clients].sort((a, b) => Number(idle(a)) - Number(idle(b)) || a.gross_margin - b.gross_margin || a.client_name.localeCompare(b.client_name));
}

/** The people behind a client and the area it serves (public.dashboard_client_profiles, rnrinfra 20261011234500). */
export type ClientProfile = { business_id: string; company: string; people: string[]; area: string | null };

const plain = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

/** How a client row is named: the person first, the company under it when it says something more, and the area. */
export function whoIs(id: string, company: string, profiles: ReadonlyMap<string, ClientProfile>): { person: string; company: string | null; area: string | null } {
  const profile = profiles.get(id);
  const people = (profile?.people ?? []).map(p => p.trim()).filter(Boolean);
  const person = people.length > 2 ? `${people.slice(0, 2).join(', ')} +${people.length - 2}` : people.join(' & ');
  if (!person) return { person: company, company: null, area: profile?.area ?? null };
  return { person, company: plain(person) === plain(company) ? null : company, area: profile?.area ?? null };
}

export type PaceGroup = 'behind' | 'on_pace' | 'met' | 'no_goal';
/** The four groups the client list filters by: on pace takes in on track and ahead. */
export const paceGroup = (pace: Pace): PaceGroup => (pace === 'behind' || pace === 'met' || pace === 'no_goal' ? pace : 'on_pace');

/** The portfolio against its targets: only clients with a monthly lead target count towards it. */
export function portfolioPace(clients: KpiClient[]) {
  const goaled = clients.filter(c => c.goal !== null && c.goal > 0);
  const sum = (f: (c: KpiClient) => number | null) => goaled.reduce((n, c) => n + (f(c) ?? 0), 0);
  return {
    goal: sum(c => c.goal), leads: sum(c => c.leads), expected: sum(c => c.expected), projected: sum(c => c.projected_leads),
    withGoal: goaled.length,
    behind: goaled.filter(c => c.pace === 'behind').length,
    onPace: goaled.filter(c => c.pace !== 'behind').length,
    met: goaled.filter(c => c.pace === 'met').length,
    noGoal: clients.length - goaled.length,
  };
}

/** Cost per lead, or null with no leads. */
export const perLead = (spend: number, leads: number): number | null => (leads > 0 ? spend / leads : null);

export function closeRate(talked: number, closed: number): string {
  return talked > 0 ? `${Math.round((100 * closed) / talked)}%` : '—';
}

/** The form's fields: what a draft sends to dashboard_save_client_terms. Blank inputs are sent as null. */
export type TermsDraft = Record<Exclude<keyof ClientTerms, 'business_id' | 'revision' | 'updated_at'>, string> & { reason: string };
export function toDraft(t: ClientTerms): TermsDraft {
  const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));
  return {
    stage: t.stage, billing_model: s(t.billing_model), monthly_fee: s(t.monthly_fee), price_per_lead: s(t.price_per_lead),
    monthly_lead_goal: s(t.monthly_lead_goal), client_monthly_revenue: s(t.client_monthly_revenue), trial_started_on: s(t.trial_started_on),
    trial_lead_goal: s(t.trial_lead_goal), trial_ended_on: s(t.trial_ended_on), trial_outcome: s(t.trial_outcome), closer_id: s(t.closer_id),
    talked_on: s(t.talked_on), closed_on: s(t.closed_on), paying_since: s(t.paying_since), cancelled_on: s(t.cancelled_on),
    cancel_reason: s(t.cancel_reason), notes: s(t.notes), reason: '',
  };
}
export function draftRequest(d: TermsDraft, revision: number): Record<string, string | number | null> {
  const blank = (v: string) => (v.trim() === '' ? null : v.trim());
  const req: Record<string, string | number | null> = { expected_revision: revision };
  for (const [key, value] of Object.entries(d)) req[key] = blank(value);
  req.stage = d.stage || 'unknown';
  return req;
}

/** Which parts of the form matter for a stage. */
export function stageFields(stage: string) {
  return {
    billing: stage !== 'trial_ended',
    trial: stage === 'trial' || stage === 'trial_ended' || stage === 'paying',
    outcome: stage === 'trial_ended',
    cancel: stage === 'cancelled',
  };
}
